/**
 * Utilitário centralizado para gerenciamento de localStorage
 * Fornece type-safety e tratamento de erros consistente
 */

/**
 * Chaves padronizadas do localStorage
 */
export const StorageKeys = {
  // Autenticação
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  
  // Cache de dados
  USER_NAMES_CACHE: 'userNamesCache',
  
  // Preferências de UI
  PLANT_COMMENTS_FILTER: 'plantComments_filterMode',
  SCHEDULE_SHOW_EXTRA_SLOTS: 'scheduleShowExtraSlots',
  SCHEDULE_COMPACT_MODE: 'scheduleCompactMode',
  
  // ResourceList (dinâmico - usa função helper)
  resourceListRowsPerPage: (key: string) => `resourceList_${key}_rowsPerPage`,
} as const;

/**
 * Tipo para valores de filtro de comentários
 */
export type PlantCommentsFilter = 'all' | 'comments' | 'actions';

/**
 * Interface genérica para cache com timestamp
 */
interface CachedData<T> {
  data: T;
  timestamp: number;
}

/**
 * Classe utilitária para gerenciamento de localStorage
 */
class StorageService {
  /**
   * Salva um valor no localStorage
   */
  set<T>(key: string, value: T): void {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
    } catch (error) {
      console.error(`[Storage] Erro ao salvar ${key}:`, error);
    }
  }

  /**
   * Recupera um valor do localStorage
   */
  get<T>(key: string, defaultValue?: T): T | null {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue !== undefined ? defaultValue : null;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`[Storage] Erro ao ler ${key}:`, error);
      return defaultValue !== undefined ? defaultValue : null;
    }
  }

  /**
   * Salva um valor string diretamente (sem JSON.stringify)
   */
  setString(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`[Storage] Erro ao salvar string ${key}:`, error);
    }
  }

  /**
   * Recupera um valor string diretamente
   */
  getString(key: string, defaultValue?: string): string | null {
    try {
      const value = localStorage.getItem(key);
      return value !== null ? value : (defaultValue ?? null);
    } catch (error) {
      console.error(`[Storage] Erro ao ler string ${key}:`, error);
      return defaultValue ?? null;
    }
  }

  /**
   * Remove um item do localStorage
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`[Storage] Erro ao remover ${key}:`, error);
    }
  }

  /**
   * Limpa todo o localStorage
   */
  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('[Storage] Erro ao limpar localStorage:', error);
    }
  }

  /**
   * Verifica se uma chave existe
   */
  has(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Salva cache com timestamp (útil para invalidação)
   */
  setWithTimestamp<T>(key: string, data: T): void {
    const cached: CachedData<T> = {
      data,
      timestamp: Date.now(),
    };
    this.set(key, cached);
  }

  /**
   * Recupera cache verificando validade (em ms)
   */
  getWithTimestamp<T>(key: string, maxAge?: number): T | null {
    const cached = this.get<CachedData<T>>(key);
    
    if (!cached) {
      return null;
    }

    // Se maxAge foi especificado, verifica se ainda é válido
    if (maxAge !== undefined) {
      const age = Date.now() - cached.timestamp;
      if (age > maxAge) {
        this.remove(key); // Remove cache expirado
        return null;
      }
    }

    return cached.data;
  }

  // ========================================
  // Métodos específicos por funcionalidade
  // ========================================

  /**
   * Filtro de comentários de planta
   */
  getPlantCommentsFilter(): PlantCommentsFilter {
    const value = this.getString(StorageKeys.PLANT_COMMENTS_FILTER);
    if (value && ['all', 'comments', 'actions'].includes(value)) {
      return value as PlantCommentsFilter;
    }
    return 'all';
  }

  setPlantCommentsFilter(filter: PlantCommentsFilter): void {
    this.setString(StorageKeys.PLANT_COMMENTS_FILTER, filter);
  }

  /**
   * Rows per page do ResourceList
   */
  getResourceListRowsPerPage(resourceKey: string, defaultValue: number = 10): number {
    const key = StorageKeys.resourceListRowsPerPage(resourceKey);
    const value = this.getString(key);
    if (value) {
      const parsed = parseInt(value, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return defaultValue;
  }

  setResourceListRowsPerPage(resourceKey: string, rowsPerPage: number): void {
    const key = StorageKeys.resourceListRowsPerPage(resourceKey);
    this.setString(key, String(rowsPerPage));
  }

  /**
   * Preferências de agendamento
   */
  getScheduleShowExtraSlots(): boolean {
    const value = this.getString(StorageKeys.SCHEDULE_SHOW_EXTRA_SLOTS);
    return value === 'true';
  }

  setScheduleShowExtraSlots(show: boolean): void {
    this.setString(StorageKeys.SCHEDULE_SHOW_EXTRA_SLOTS, String(show));
  }

  getScheduleCompactMode(): boolean {
    const value = this.getString(StorageKeys.SCHEDULE_COMPACT_MODE);
    return value === 'true';
  }

  setScheduleCompactMode(compact: boolean): void {
    this.setString(StorageKeys.SCHEDULE_COMPACT_MODE, String(compact));
  }

  /**
   * Cache de nomes de usuários
   */
  getUserNamesCache(): Record<string, string> | null {
    return this.get<Record<string, string>>(StorageKeys.USER_NAMES_CACHE);
  }

  setUserNamesCache(names: Record<string, string>): void {
    this.set(StorageKeys.USER_NAMES_CACHE, names);
  }

  /**
   * Token de autenticação
   */
  getAccessToken(): string | null {
    return this.getString(StorageKeys.ACCESS_TOKEN);
  }

  setAccessToken(token: string): void {
    this.setString(StorageKeys.ACCESS_TOKEN, token);
  }

  removeAccessToken(): void {
    this.remove(StorageKeys.ACCESS_TOKEN);
  }

  getRefreshToken(): string | null {
    return this.getString(StorageKeys.REFRESH_TOKEN);
  }

  setRefreshToken(token: string): void {
    this.setString(StorageKeys.REFRESH_TOKEN, token);
  }

  removeRefreshToken(): void {
    this.remove(StorageKeys.REFRESH_TOKEN);
  }

  /**
   * Dados do usuário autenticado
   */
  getUser<T = any>(): T | null {
    return this.get<T>('user');
  }

  setUser<T = any>(user: T): void {
    this.set('user', user);
  }

  removeUser(): void {
    this.remove('user');
  }
}

// Exporta instância singleton
export const storage = new StorageService();

// Exporta também a classe para testes
export { StorageService };
